import {
  useOrganizationInvitations,
  type Invitation,
} from '@/organization/useOrganizations';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { InvitationDetailsDialog } from './InvitationDetailsDialog';

interface Props {
  organizationId: string;
}

export function InvitationsList({ organizationId }: Props) {
  const {
    data: invitations,
    isLoading,
    error,
  } = useOrganizationInvitations(organizationId);
  const [selectedInvitation, setSelectedInvitation] =
    useState<Invitation | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyLink = async (invitation: Invitation) => {
    try {
      await navigator.clipboard.writeText(invitation.signupUrl);
      setCopiedId(invitation.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusChip = (invitation: Invitation) => {
    if (invitation.usedAt) {
      return <Chip label='Used' color='success' size='small' />;
    }
    if (invitation.isExpired) {
      return <Chip label='Expired' color='error' size='small' />;
    }
    return <Chip label='Pending' color='primary' size='small' />;
  };

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ m: 2 }}>
        Failed to load invitations
      </Alert>
    );
  }

  if (!invitations || invitations.length === 0) {
    return (
      <Box p={3} textAlign='center'>
        <Typography color='text.secondary'>No invitations sent yet</Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant='outlined'>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent By</TableCell>
              <TableCell>Sent Date</TableCell>
              <TableCell>Used By</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map((invitation) => (
              <TableRow key={invitation.id}>
                <TableCell>{invitation.email}</TableCell>
                <TableCell>{getStatusChip(invitation)}</TableCell>
                <TableCell>{invitation.createdByName}</TableCell>
                <TableCell>
                  {format(new Date(invitation.createdAt), 'MMM d, yyyy h:mm a')}
                </TableCell>
                <TableCell>{invitation.usedByName || '-'}</TableCell>
                <TableCell align='right'>
                  <Stack direction='row' spacing={1} justifyContent='flex-end'>
                    <Tooltip title='Copy invite link'>
                      <IconButton
                        size='small'
                        onClick={() => handleCopyLink(invitation)}
                        disabled={!invitation.isValid}
                        color={
                          copiedId === invitation.id ? 'success' : 'default'
                        }
                      >
                        {copiedId === invitation.id ? (
                          <CheckIcon fontSize='small' />
                        ) : (
                          <ContentCopyIcon fontSize='small' />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='View details'>
                      <IconButton
                        color={'info'}
                        size='small'
                        onClick={() => setSelectedInvitation(invitation)}
                      >
                        <VisibilityIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedInvitation && (
        <InvitationDetailsDialog
          open={true}
          onClose={() => setSelectedInvitation(null)}
          invitation={selectedInvitation}
        />
      )}
    </>
  );
}
